// Reloj del servidor (D-06): la web nunca decide qué día es. Guarda la diferencia con el reloj local a partir
// de `serverNow` de `/session` y del header `X-Server-Now` de cada respuesta, y la usa solo para mostrar.
import { useEffect, useState, useSyncExternalStore } from 'react';

let offsetMs = 0;
let known = false;
const listeners = new Set<() => void>();

/** Registra el instante del servidor (ISO 8601). Ignora valores inválidos. */
export function setServerNow(iso: string | null | undefined, receivedAt = Date.now()): void {
  if (!iso) return;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return;
  const next = t - receivedAt;
  // Evita re-renderizar por diferencias de red de pocos ms.
  if (known && Math.abs(next - offsetMs) < 1000) return;
  offsetMs = next;
  known = true;
  listeners.forEach((l) => l());
}

/** Diferencia servidor − navegador en ms. */
export function serverOffsetMs(): number {
  return offsetMs;
}

/** Instante actual según el servidor. */
export function serverNow(): Date {
  return new Date(Date.now() + offsetMs);
}

const partsCache = new Map<string, Intl.DateTimeFormat>();
function zoneFormatter(timezone: string): Intl.DateTimeFormat {
  let f = partsCache.get(timezone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    partsCache.set(timezone, f);
  }
  return f;
}

function zoned(date: Date, timezone: string): { date: string; time: string } {
  const p: Record<string, string> = {};
  for (const part of zoneFormatter(timezone).formatToParts(date)) p[part.type] = part.value;
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

/** Día de negocio actual en una zona (solo para mostrar; el servidor manda `today`). Mismo cálculo que `businessDateOf` del dominio. */
export function serverToday(timezone: string): string {
  return zoned(serverNow(), timezone).date;
}

/** Hora actual "HH:MM" en una zona. */
export function serverClock(timezone: string): string {
  return zoned(serverNow(), timezone).time;
}

const WEEKDAYS_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

/** "vie 7/11" desde YYYY-MM-DD (igual que `dateWeekdayShort` del dominio, sin cargarlo). */
export function weekdayShortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${WEEKDAYS_SHORT[wd]} ${d}/${m}`;
}

function subscribeOffset(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Diferencia reactiva (cambia si el servidor corrige el reloj). */
export function useServerOffset(): number {
  return useSyncExternalStore(subscribeOffset, serverOffsetMs, serverOffsetMs);
}

/**
 * Re-renderiza al empezar cada minuto (timer alineado al minuto, UX-P50). Usar solo en componentes que
 * dependen de la hora ("hace 5 min", línea de ahora), no en la raíz.
 */
export function useMinuteTick(): Date {
  useServerOffset();
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const n = serverNow();
      const ms = 60_000 - (n.getSeconds() * 1000 + n.getMilliseconds()) + 20;
      timer = setTimeout(() => {
        setNow(serverNow());
        schedule();
      }, ms);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);
  return now;
}

/** "hace 5 min", "hace 2 h", "hace 3 días" (relativo al reloj del servidor). */
export function relativeFromNow(iso: string, now: Date = serverNow()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const min = Math.max(0, Math.round((now.getTime() - t) / 60_000));
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
}
